import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged,
  User,
} from "firebase/auth";
import { auth, db } from "./config";
import { doc, getDoc } from "firebase/firestore";
import { COLLECTIONS } from "../constants";
import { UserRole } from "../constants";

export interface AuthUser extends User {
  role?: UserRole;
  storeId?: string;
  fournisseurId?: string;
  name?: string;
  username?: string;
  /** Support autorise a gerer les fournisseurs (18/10/2026). */
  accesFournisseurs?: boolean;
}

/**
 * Trouver l'email à partir du username via API (sécurisé)
 */
async function getEmailFromUsername(username: string): Promise<string> {
  const API_BASE_URL =
    import.meta.env.VITE_API_URL || "https://api-a2drw3jgqa-ew.a.run.app";

  const response = await fetch(`${API_BASE_URL}/api/auth/login-username`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ username }),
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("Nom d'utilisateur ou mot de passe incorrect");
    }
    throw new Error("Erreur lors de la connexion");
  }

  const userData = await response.json();

  if (!userData.email) {
    throw new Error("Email non trouvé pour cet utilisateur");
  }

  return userData.email;
}

/**
 * Envoie a une adresse le lien qui permet de choisir un mot de passe.
 *
 * Le meme lien sert aux deux besoins : « initialise ton mot de passe » a la
 * creation du compte, et « j'ai oublie le mien » plus tard. C'est
 * volontaire — deux mecanismes pour une seule chose, c'est deux fois plus de
 * pannes possibles.
 *
 * C'est Firebase qui envoie l'e-mail : aucun serveur d'envoi a installer.
 *
 * Important : cette fonction ne dit JAMAIS si l'adresse existe ou non. Sinon
 * n'importe qui pourrait, en essayant des adresses une a une, dresser la
 * liste de tes collaborateurs. Elle repond toujours la meme chose.
 */
export async function envoyerLienMotDePasse(email: string): Promise<void> {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (e: any) {
    // On journalise pour toi, on ne revele rien a l'ecran.
    console.warn("Lien mot de passe non envoye :", e?.code || e?.message);
  }
}

/**
 * Connexion avec username/password
 * Firebase Auth nécessite un email, donc on trouve l'email à partir du username
 */
export async function login(
  username: string,
  password: string,
): Promise<AuthUser> {
  // Trouver l'email correspondant au username
  const email = await getEmailFromUsername(username);

  // Authentifier avec email/password
  const userCredential = await signInWithEmailAndPassword(
    auth,
    email,
    password,
  );
  const user = userCredential.user;

  // Récupérer les données utilisateur depuis Firestore
  const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, user.uid));
  if (!userDoc.exists()) {
    throw new Error("User data not found");
  }

  const userData = userDoc.data();
  const authUser: AuthUser = {
    ...user,
    role: userData.role,
    name: userData.name,
    username: userData.username,
    accesFournisseurs: userData.accesFournisseurs === true,
  };

  // Récupérer les données spécifiques selon le rôle
  if (userData.role === UserRole.COLLABORATEUR) {
    const collabDoc = await getDoc(
      doc(db, COLLECTIONS.COLLABORATEURS, user.uid),
    );
    if (collabDoc.exists()) {
      authUser.storeId = collabDoc.data().storeId;
    }
  } else if (userData.role === UserRole.FOURNISSEUR) {
    const fournDoc = await getDoc(doc(db, COLLECTIONS.FOURNISSEURS, user.uid));
    if (fournDoc.exists()) {
      authUser.storeId = fournDoc.data().storeId;
      authUser.fournisseurId = fournDoc.id;
    }
  }

  return authUser;
}

/**
 * Déconnexion
 */
export async function logout(): Promise<void> {
  await signOut(auth);
}

/**
 * Observer les changements d'authentification
 */
export function onAuthChange(
  callback: (user: AuthUser | null) => void,
): () => void {
  return onAuthStateChanged(auth, async (user) => {
    if (!user) {
      callback(null);
      return;
    }

    try {
      const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, user.uid));
      if (!userDoc.exists()) {
        callback(null);
        return;
      }

      const userData = userDoc.data();
      const authUser: AuthUser = {
        ...user,
        role: userData.role,
        name: userData.name,
        username: userData.username,
        accesFournisseurs: userData.accesFournisseurs === true,
      };

      // Récupérer les données spécifiques selon le rôle
      if (userData.role === UserRole.COLLABORATEUR) {
        const collabDoc = await getDoc(
          doc(db, COLLECTIONS.COLLABORATEURS, user.uid),
        );
        if (collabDoc.exists()) {
          authUser.storeId = collabDoc.data().storeId;
        }
      } else if (userData.role === UserRole.FOURNISSEUR) {
        const fournDoc = await getDoc(
          doc(db, COLLECTIONS.FOURNISSEURS, user.uid),
        );
        if (fournDoc.exists()) {
          authUser.storeId = fournDoc.data().storeId;
          authUser.fournisseurId = fournDoc.id;
        }
      }

      callback(authUser);
    } catch (error) {
      console.error("Error fetching user data", error);
      callback(null);
    }
  });
}
